/* eslint-disable jsx-a11y/anchor-is-valid */
import objectPath from "object-path";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import SVG from "react-inlinesvg";
import { NavLink } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import AuthService from "../../../../app/base/services/authentication.service";
import { toAbsoluteUrl } from "../../../_helpers";
// import { isDarkMode, toggleDarkMode } from "../../../_helpers/DarkModeHelper";
import { useHtmlClassService } from "../../_core/MetronicLayout";
import { Brand } from "../brand/Brand";
import { LanguageSelectorDropdown } from "../extras/dropdowns/LanguageSelectorDropdown";
import { AsideMenu } from "./aside-menu/AsideMenu";
import { UserRolesEnum } from "../../../../app/pages/auth/userRoles.enum";
import { mainMenu, translateMenuTitle } from "../../../../app/MainMenu";
import { GoBack } from "../../../../app/assets/icons";
import useAsideStore from "./AsideStore";
import { isSecondaryMenuCollapseRoute } from "./secondaryMenuRoute.utils";
import { useTranslation } from "../../../../app/i18n/LanguageContext";

export function Aside() {
   const { t } = useTranslation();
   const location = useLocation();
   const currentPathID = location.pathname.split("/").slice(1)[0];
   const navigate = useNavigate();
   const isOrganizationManagement = location.pathname.includes("organization-management");
   const isGuidedConsultant =
      (location.pathname.endsWith("list") ||
         location.pathname.includes("/guided-assessment/list") ||
         location.pathname.includes("/guided-assessment/surveys/") ||
         (location.pathname.includes("/guided-assessment/") &&
            location.pathname.includes("/survey/"))) &&
      AuthService.isAuthorized([UserRolesEnum.Consultant]);
   const isStaff = AuthService.isAuthorized([UserRolesEnum.Staff]);
   const isManagerActionPlanView =
      location.pathname.includes("workspace/survey-request/") &&
      AuthService.isAuthorized([UserRolesEnum.Manager]);
   const isManagerResourcesView =
      location.pathname.includes("workspace/resources") &&
      AuthService.isAuthorized([
         UserRolesEnum.NationalOrganization,
         UserRolesEnum.SuperAdmin,
         UserRolesEnum.Manager,
         UserRolesEnum.Staff
      ]);
   const isHelpDesk =
      location.pathname.includes("help-desk/list") ||
      location.pathname.includes("help-desk/details");
   const isActionPlanOfSurvey =
      location.pathname.includes("/workspace/results/action-plan/survey-results") &&
      AuthService.isAuthorized([UserRolesEnum.NationalOrganization, UserRolesEnum.Manager]);

   const menuItemRef = useRef(null);
   const user = AuthService.getUser();
   const uiService = useHtmlClassService();
   const { collapsedSecodary, hiddenSecondary } = useAsideStore();
   const canApplyCollapsedSecondary = isSecondaryMenuCollapseRoute(location.pathname);

   const layoutProps = useMemo(() => {
      return {
         asideClassesFromConfig: uiService.getClasses("aside", true),
         asideSecondaryDisplay: objectPath.get(uiService.config, "aside.secondary.display"),
         asideSelfMinimizeToggle: objectPath.get(uiService.config, "aside.self.minimize.toggle"),
         extrasSearchDisplay: objectPath.get(uiService.config, "extras.search.display"),
         extrasNotificationsDisplay: objectPath.get(
            uiService.config,
            "extras.notifications.display"
         ),
         extrasQuickActionsDisplay: objectPath.get(
            uiService.config,
            "extras.quick-actions.display"
         ),
         extrasQuickPanelDisplay: objectPath.get(uiService.config, "extras.quick-panel.display"),
         extrasLanguagesDisplay: objectPath.get(uiService.config, "extras.languages.display"),
         extrasUserDisplay: objectPath.get(uiService.config, "extras.user.display")
      };
   }, [uiService]);

   const [activeTab, setActiveTab] = useState(() => {
      return currentPathID;
   });

   // const [isDarkModeOn, setisDarkModeOn] = useState(isDarkMode());

   useEffect(() => {
      setActiveTab(currentPathID);
   }, [currentPathID]);

   const handleTabChange = (id) => {
      let newId = id;
      setActiveTab(newId);
      if (isMobile) {
         setShowSecondary(true);
      }
   };

   const logout = () => {
      AuthService.logout();
      navigate("/auth/login");
   };

   // const helpDesk = () => {
   //    const organizationId = AuthService.getUser()?.organizationId;
   //    if(user)
   //    // document.body.classList.add("aside-minimize");
   //    navigate(`/help-desk/list?organizationId=${organizationId}`);
   // };

   const helpDesk = () => {
      if (user.roles[0].name === "SuperAdmin") {
         navigate(`/admin/help-desk/list`);
      } else {
         navigate(`/workspace/help-desk/list`);
      }
   };
   const isMobile = useIsMobile();
   const isIpad = useIsIpad();

   useEffect(() => {
      const pathname = location?.pathname;

      let newActiveTab = null;

      if (pathname.includes("/assessment/self-assessment/")) {
         newActiveTab = "self-assessments";
      } else if (
         pathname.includes("/assessment/guided-assessment/") &&
         !pathname.includes("list")
      ) {
         newActiveTab = "guided-assessment";
      } else if (pathname.includes("/results/results")) {
         newActiveTab = "results";
      } else if (
         pathname.includes("/workspace/results/action-plan") &&
         !pathname.includes("member")
      ) {
         newActiveTab = "action-plan";
      } else if (pathname.includes("/help-desk/list")) {
         newActiveTab = "help-desk";
      }

      setActiveTab(newActiveTab);

      const hasSecondary = [
         "self-assessments",
         "guided-assessment",
         "results",
         "action-plan"
      ].includes(newActiveTab);

      if (isMobile) {
         setShowSecondary(hasSecondary);
      }
   }, [location.pathname, isMobile]);

   function useIsMobile(breakpoint = 540) {
      const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);

      useEffect(() => {
         const handleResize = () => {
            setIsMobile(window.innerWidth <= breakpoint);
         };
         window.addEventListener("resize", handleResize);
         return () => window.removeEventListener("resize", handleResize);
      }, [breakpoint]);

      return isMobile;
   }

   function useIsIpad(breakpoint = 991) {
      const [isIpad, setIsIpad] = useState(window.innerWidth <= breakpoint);

      useEffect(() => {
         const handleResize = () => {
            setIsIpad(window.innerWidth <= breakpoint);
         };
         window.addEventListener("resize", handleResize);
         return () => window.removeEventListener("resize", handleResize);
      }, [breakpoint]);

      return isMobile;
   }

   const [showSecondary, setShowSecondary] = useState(false);

   const handleBackToPrimary = () => {
      setShowSecondary(false);
      setActiveTab(null);
   };

   const shouldShowSecondaryMenu = (url, isMobile) => {
      if (!isMobile) return false;

      if (url.startsWith("/workspace/assessment/guided-assessment/")) {
         return !url.endsWith("/list");
      }

      if (url.startsWith("/workspace/assessment/self-assessment/")) {
         return true;
      }

      if (url.includes("/workspace/results/action-plan") && !url.includes("action-plan-member")) {
         return true;
      }

      if (url.startsWith("/workspace/results/results/") && url.split("/").length > 4) {
         return true;
      }

      return false;
   };

   return (
      <>
         {/* begin::Aside */}
         <div
            id="kt_aside"
            className={`aside aside-left d-flex ${layoutProps.asideClassesFromConfig} ${
               isIpad
                  ? "workspace-aside"
                  : isOrganizationManagement ||
                    isHelpDesk ||
                    isManagerActionPlanView ||
                    isManagerResourcesView ||
                    isActionPlanOfSurvey ||
                    isGuidedConsultant ||
                     isStaff
                  ? "workspace-aside-organization-management"
                  : "workspace-aside"
            } ${collapsedSecodary && canApplyCollapsedSecondary && "collapsed-secondary"} ${hiddenSecondary &&
               "hidden-secondary"}`}
            style={{
               ...(isMobile && !showSecondary ? { overflow: "hidden" } : {})
            }}
         >
            {/* begin::Primary */}
            {(!isMobile || !showSecondary) && (
               <div className="aside-primary d-flex flex-column flex-row-auto">
                  <Brand />
                  <div className="aside-nav d-flex flex-column align-items-center flex-column-fluid py-5 scroll scroll-pull">
                     <ul className={`list-unstyled flex-column`} role="tablist">
                        {mainMenu.layout?.map((item, i) =>
                           AuthService.isAuthorized(item?.role ? item.role : []) ? (
                              <div key={i}>
                                 {/* {AuthService.isAuthorized([UserRolesEnum.Manager]) ? (
                                    ""
                                 ) : ( */}
                                    <h4>{translateMenuTitle(item.title, t)}</h4>
                                 {/* )} */}

                                 {item.children.map((child) =>
                                    AuthService.isAuthorized(child?.role ? child.role : []) ? (
                                       child.type !== "banner" ? (
                                          <li
                                             key={child.id}
                                             className={`nav-item mb-3`}
                                             data-toggle="tooltip"
                                             data-placement="right"
                                             data-container="body"
                                             data-boundary="window"
                                          >
                                             {/* remove full url */}
                                             {(() => {
                                                const fullUrl = AuthService.isAuthorized(
                                                   child?.adminRole || []
                                                )
                                                   ? child?.adminUrl
                                                   : AuthService.isAuthorized([
                                                        UserRolesEnum.Consultant
                                                     ]) && child?.url?.includes("guided-assessment")
                                                   ? "/workspace/assessment/guided-assessment/list"
                                                   : child?.url?.includes("action-plan") &&
                                                     AuthService.isAuthorized([UserRolesEnum.Staff])
                                                   ? "/workspace/results/action-plan-member/"
                                                   : child?.url?.includes("action-plan") &&
                                                     !AuthService.isAuthorized([
                                                        UserRolesEnum.Staff
                                                     ])
                                                   ? "/workspace/results/action-plan"
                                                   : child?.url;

                                                return (
                                                   <NavLink
                                                      exact="true"
                                                      ref={menuItemRef}
                                                      to={fullUrl}
                                                      className={`nav-link ${
                                                         location.pathname.includes(child.url)
                                                            ? "active"
                                                            : ""
                                                      }`}
                                                      onClick={(e) => {
                                                         e.preventDefault();
                                                         handleTabChange(child.id);

                                                         const showSecondary = shouldShowSecondaryMenu(
                                                            fullUrl,
                                                            isMobile
                                                         );
                                                         setShowSecondary(showSecondary);

                                                         if (location.pathname !== fullUrl) {
                                                            navigate(fullUrl);
                                                         }
                                                      }}
                                                   >
                                                      <div className="svg-icon svg-icon-lg">
                                                         <img
                                                            src={
                                                               process.env.PUBLIC_URL +
                                                               "/media/svg/icons/General/" +
                                                               child.icon
                                                            }
                                                            alt="icons"
                                                         />
                                                         <span className="nav-title">
                                                            {translateMenuTitle(child.title, t)}
                                                         </span>
                                                      </div>

                                                      {AuthService.isAuthorized([
                                                         UserRolesEnum.Consultant,
                                                         UserRolesEnum.Staff
                                                      ]) ? (
                                                         <></>
                                                      ) : AuthService.isAuthorized([
                                                           UserRolesEnum.Manager
                                                        ]) &&
                                                        location.pathname.includes(
                                                           "/survey-request/"
                                                        ) ? (
                                                         <></>
                                                      ) : (
                                                         <img
                                                            className={`arrow-menu`}
                                                            src={
                                                               process.env.PUBLIC_URL +
                                                               "/media/svg/icons/General/menuArrow.svg"
                                                            }
                                                            alt="menu arrow"
                                                         />
                                                      )}
                                                   </NavLink>
                                                );
                                             })()}
                                          </li>
                                       ) : (
                                          <li
                                             key={child.id}
                                             className={`nav-banner ${activeTab === child.id &&
                                                "active"}`}
                                             data-toggle="tooltip"
                                             data-placement="right"
                                             data-container="body"
                                             data-boundary="window"
                                          >
                                             <NavLink
                                                to={child.link}
                                                className={`nav-link-banner`}
                                                activeclassname="active"
                                                onClick={() => handleTabChange(child.id)}
                                             >
                                                <div className="nav-link-banner-wrap">
                                                   <span>{translateMenuTitle(child.title, t)}</span>
                                                   <img
                                                      src={
                                                         process.env.PUBLIC_URL +
                                                         "/media/svg/icons/General/" +
                                                         child.icon
                                                      }
                                                      alt="icons"
                                                   />
                                                </div>
                                             </NavLink>
                                          </li>
                                       )
                                    ) : (
                                       <></>
                                    )
                                 )}
                              </div>
                           ) : (
                              <React.Fragment key={i}></React.Fragment>
                           )
                        )}
                     </ul>
                     {/* end::Nav */}
                  </div>
                  <div className="aside-footer">
                     {/* begin::Search */}
                     {layoutProps.extrasSearchDisplay && (
                        <OverlayTrigger
                           placement="right"
                           overlay={<Tooltip id="toggle-search">{t("quick-search-tooltip-text")}</Tooltip>}
                        >
                           <a
                              className="btn btn-icon btn-clean btn-lg mb-1"
                              id="kt_quick_search_toggle"
                           >
                              <span className="svg-icon svg-icon-lg">
                                 <SVG src={toAbsoluteUrl("/media/svg/icons/General/Search.svg")} />
                              </span>
                           </a>
                        </OverlayTrigger>
                     )}
                     {/* end::Search */}

                     {/* begin::Notifications */}
                     {layoutProps.extrasNotificationsDisplay && (
                        <OverlayTrigger
                           placement="right"
                           overlay={<Tooltip id="toggle-notifications">{t("notifications-tooltip-text")}</Tooltip>}
                        >
                           <a
                              className="btn btn-icon btn-clean btn-lg mb-1 position-relative"
                              id="kt_quick_notifications_toggle"
                              data-placement="right"
                              data-container="body"
                              data-boundary="window"
                           >
                              <span className="svg-icon svg-icon-lg">
                                 <SVG src={toAbsoluteUrl("/media/svg/icons/Design/Layers.svg")} />
                              </span>
                           </a>
                        </OverlayTrigger>
                     )}
                     {/* end::Notifications */}
                     {/* Help Desk */}
                     {AuthService.isAuthorized([UserRolesEnum.NationalOrganization]) ||
                     AuthService.isAuthorized([UserRolesEnum.InternationalOrganization]) ? (
                        <div
                           className="nav-footer-help-desk-item"
                           data-placement="right"
                           data-container="body"
                           data-boundary="window"
                        >
                           <a
                              data-placement="right"
                              data-container="body"
                              data-boundary="window"
                              className={`nav-footer-help-desk ${
                                 activeTab === "help-desk" ? "active" : ""
                              }`}
                              onClick={() => {
                                 setActiveTab("help-desk");
                                 helpDesk();
                              }}
                           >
                              <div className="nav-footer-item-wrap-help-desk">
                                 <img
                                    src={
                                       process.env.PUBLIC_URL +
                                       "/media/svg/icons/General/tickets.svg"
                                    }
                                    alt="icons"
                                 />
                                 <span>
                                    {user?.roles?.length
                                       ? user?.roles[0]?.name === "SuperAdmin"
                                          ? t("menu-item-tickets")
                                          : t("help-desk-word")
                                       : ""}
                                 </span>
                              </div>
                           </a>
                        </div>
                     ) : null}
                     {layoutProps.extrasLanguagesDisplay && <LanguageSelectorDropdown />}
                  </div>
               </div>
            )}

            {layoutProps.asideSecondaryDisplay && (!isMobile || showSecondary) && (
               <>
                  {isGuidedConsultant || isStaff || isManagerActionPlanView ? (
                     <></>
                  ) : (
                     <div
                        className={`${
                           isOrganizationManagement || isHelpDesk || isManagerActionPlanView
                              ? "organization-management-class"
                              : "aside-secondary"
                        } d-flex flex-row-fluid`}
                     >
                        <div className="aside-workspace scroll scroll-push my-2">
                           <div className="tab-content">
                              {isMobile && (
                                 <div className="back-button p-3">
                                    <button
                                       className="d-flex align-items-center"
                                       onClick={handleBackToPrimary}
                                       style={{
                                          border: "none",
                                          background: "none",
                                          color: "#777777",
                                          fontSize: "14px",
                                          fontWeight: 500
                                       }}
                                    >
                                       <img
                                          src={GoBack}
                                          alt="GoBack"
                                          style={{ marginRight: "10px" }}
                                       />{" "}
                                       {t("go-back-previous-menu-text")}
                                    </button>
                                 </div>
                              )}
                              <AsideMenu activeMenu={activeTab} layoutProps={layoutProps} />
                           </div>
                        </div>
                     </div>
                  )}
               </>
            )}
         </div>
         {/* end::Aside */}
      </>
   );
}
